import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage, useTranslation } from '@/hooks/useTranslation';
import type { SupportedLanguage } from '@/i18n/types';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '@/i18n/types';
import { Globe } from 'lucide-react';

interface LanguageSelectorProps {
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showText?: boolean;
  className?: string;
}

/**
 * Language selector component with dropdown menu
 * Follows the project's component patterns and uses Shadcn/ui components
 */
export function LanguageSelector({
  variant = 'outline',
  size = 'icon',
  showText = false,
  className,
}: LanguageSelectorProps) {
  const { currentLanguage, setLanguage, isLoading } = useLanguage();
  const { t } = useTranslation('common');

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (language !== currentLanguage && !isLoading) {
      await setLanguage(language);
    }
  };

  const getCurrentLanguageName = () => {
    return (
      LANGUAGE_NAMES[currentLanguage as SupportedLanguage] || currentLanguage
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={isLoading}
        >
          <Globe className='h-[1.2rem] w-[1.2rem]' />
          {showText && <span className='ml-2'>{getCurrentLanguageName()}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='space-y-1'>
        {SUPPORTED_LANGUAGES.map(language => (
          <DropdownMenuItem
            key={language}
            onClick={() => handleLanguageChange(language)}
            className={
              currentLanguage === language
                ? 'bg-accent/50 text-accent-foreground'
                : ''
            }
          >
            <span className='w-8 text-sm font-medium'>
              {language.toUpperCase()}
            </span>
            <span className='ml-2'>
              {t(`language.${language}` as `language.${SupportedLanguage}`)}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
